import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, FileText, Image as ImageIcon, ShoppingBag, LayoutTemplate, FolderOpen,
  Users, Navigation as NavIcon, Sliders, PenSquare, ShieldAlert, Activity, Eye,
  KeyRound, ScanEye, Scale, Server, Database, CreditCard, Code2, LayoutDashboard, Sparkles,
} from 'lucide-react';
import axios from 'axios';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import PadaidLogo from '../../../components/ui/PadaidLogo';
import { useAuth } from '../../../contexts/AuthContext';
import API_BASE_URL from '../../../services/api';

const SECTIONS = [
  {
    title: 'Contenu du site',
    accent: 'text-primary',
    items: [
      { to: '/blog', icon: FileText, label: 'Blog & Tracts', desc: 'Publier / modifier les articles' },
      { to: '/gallery', icon: ImageIcon, label: 'Galerie', desc: 'Photos, vidéos, documentaires' },
      { to: '/shop', icon: ShoppingBag, label: 'Boutique', desc: 'Gérer les produits solidaires' },
      { to: '/admin/page-builder', icon: LayoutTemplate, label: 'Pages (Page Builder)', desc: 'Composer les pages du site' },
      { to: '/admin/bento', icon: Sparkles, label: 'Section « Explorer PADAID »', desc: 'Textes & photos de l\'accueil', highlight: true },
      { to: '/admin/backgrounds', icon: ImageIcon, label: 'Fonds d\'écran (jour/nuit)', desc: 'Choisir les photos de fond du site', highlight: true },
      { to: '/admin/backgrounds/library', icon: ImageIcon, label: 'Bibliothèque de fonds', desc: 'Pool de photos tirées au hasard sur les pages' },
      { to: '/dashboard/fondateur/floutage', icon: ScanEye, label: 'Flouter des visages', desc: 'Anonymiser les visages d\'une photo' },
      { to: '/admin/media', icon: FolderOpen, label: 'Médias', desc: 'Bibliothèque de fichiers' },
      { to: '/admin/team', icon: Users, label: 'Équipe', desc: 'Membres & profils' },
      { to: '/admin/navigation', icon: NavIcon, label: 'Navigation', desc: 'Menus & liens du site' },
      { to: '/admin/site-settings', icon: Sliders, label: 'Paramètres du site', desc: 'Statistiques, contact, réseaux' },
      { to: '/admin/editor', icon: PenSquare, label: 'Éditeur de contenu', desc: 'Textes des pages' },
    ],
  },
  {
    title: 'Comptes & Sécurité',
    accent: 'text-red-500',
    items: [
      { to: '/dashboard/fondateur/console', icon: ShieldAlert, label: 'Centre de sécurité & comptes', desc: 'Comptes, mots de passe, IP, appareils' },
      { to: '/admin/audit-logs', icon: Activity, label: 'Connexions & Inscriptions', desc: 'Journaux d\'audit' },
      { to: '/admin/audit/users', icon: Eye, label: 'Activité du site', desc: 'Pages vues & évènements' },
      { to: '/dashboard/fondateur/2fa', icon: KeyRound, label: 'Authentification 2FA', desc: 'Double facteur' },
      { to: '/dashboard/fondateur/security', icon: ScanEye, label: 'Moniteur de sécurité', desc: 'Surveillance en temps réel' },
      { to: '/dashboard/fondateur/utilisateurs', icon: Users, label: 'Utilisateurs', desc: 'Créer et gérer les comptes visiteurs' },
      { to: '/dashboard/fondateur/conformite', icon: Scale, label: 'Conformité juridique', desc: 'Veille légale IA + revue (droit français)' },
    ],
  },
  {
    title: 'Réglages & Système',
    accent: 'text-orange-500',
    items: [
      { to: '/dashboard/fondateur/system', icon: Server, label: 'Administration système', desc: 'Contrôle technique' },
      { to: '/admin/system', icon: Database, label: 'Système (avancé)', desc: 'Maintenance & données' },
      { to: '/admin/payment-settings', icon: CreditCard, label: 'Paiements', desc: 'Dons & boutique' },
      { to: '/dashboard/fondateur/code', icon: Code2, label: 'Code source', desc: 'Parcourir & copier le code', highlight: true },
    ],
  },
];

const AdminCenter = () => {
  const { token } = useAuth();
  const [legalUnread, setLegalUnread] = useState(0);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE_URL}/api/admin/legal/watch/unread`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLegalUnread(res.data?.unread_count || 0))
      .catch(() => {});
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-destructive/5" data-testid="admin-center">
      <header className="bg-card/50 backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <PadaidLogo size="md" showText={false} />
            <div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><LayoutDashboard className="h-4 w-4" /> Espace fondateur</div>
              <h1 className="text-xl font-bold">Centre d'administration</h1>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild data-testid="admin-center-back">
            <Link to="/dashboard/fondateur"><ArrowLeft className="h-4 w-4 mr-2" />Tableau de bord</Link>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        <p className="text-muted-foreground max-w-2xl">
          Gérez l'ensemble du site depuis un seul endroit. Vous ne devez passer par Emergent que pour un développement / changement majeur du code — et même dans ce cas, la section <b>Code source</b> vous permet de copier le code pour me le transmettre.
        </p>

        {SECTIONS.map((section) => (
          <section key={section.title} data-testid={`admin-section-${section.title}`}>
            <h2 className={`text-lg font-semibold mb-4 ${section.accent}`}>{section.title}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} data-testid={`admin-link-${item.to}`}>
                    <Card className={`h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${item.highlight ? 'border-primary/40 bg-primary/5' : ''}`}>
                      <CardContent className="p-5 flex items-start gap-3">
                        <div className={`rounded-xl p-2.5 ${item.highlight ? 'bg-primary/15 text-primary' : 'bg-muted text-foreground'}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-semibold flex items-center gap-2">
                            {item.label}
                            {item.to === '/dashboard/fondateur/conformite' && legalUnread > 0 && (
                              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-xs font-bold" data-testid="admin-legal-badge">{legalUnread}</span>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">{item.desc}</div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default AdminCenter;
